/**
 * Recharts visualizations for per-pump business insight modules.
 */
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatCompactInrAxis, formatInrAmount, formatIndianNumber } from '../../../utils/reportFormat';

const CHART_COLORS = ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#ca8a04', '#64748b'];

export const formatAxisCurrency = formatCompactInrAxis;

const ChartWrap = ({ height = 240, children }) => (
  <div className="admin-bi-chart-wrap" style={{ height }}>
    <ResponsiveContainer width="100%" height="100%">
      {children}
    </ResponsiveContainer>
  </div>
);

export const InsightsPanel = ({ insights = [] }) => {
  if (!insights.length) return null;
  return (
    <div className="admin-bi-insights">
      <h6 className="admin-bi-insights-title">Key insights</h6>
      <ul className="admin-bi-insights-list">
        {insights.map((item, idx) => (
          <li key={idx} className={`admin-bi-insight admin-bi-insight--${item.severity || 'info'}`}>
            <strong>{item.title}</strong>
            <span>{item.detail}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const ActivityCharts = ({ data }) => {
  const trend = data.daily_trend || [];
  const weekday = data.weekday_pattern || [];
  const fuelMix = data.fuel_mix || [];

  return (
    <div className="admin-bi-chart-grid">
      {trend.length > 0 && (
        <div className="admin-bi-chart-card">
          <h6>Daily sales trend</h6>
          <ChartWrap height={220}>
            <AreaChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tickFormatter={formatAxisCurrency} tick={{ fontSize: 11 }} width={56} />
              <Tooltip formatter={(v) => [formatInrAmount(v), 'Sales']} />
              <Area type="monotone" dataKey="sales" stroke="#2563eb" fill="#93c5fd" fillOpacity={0.5} />
            </AreaChart>
          </ChartWrap>
        </div>
      )}
      {weekday.length > 0 && (
        <div className="admin-bi-chart-card">
          <h6>Weekday pattern (avg sales)</h6>
          <ChartWrap height={220}>
            <BarChart data={weekday}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={formatAxisCurrency} tick={{ fontSize: 11 }} width={56} />
              <Tooltip formatter={(v) => [formatInrAmount(v), 'Avg sales']} />
              <Bar dataKey="avg_sales" fill="#16a34a" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartWrap>
        </div>
      )}
      {fuelMix.length > 0 && (
        <div className="admin-bi-chart-card">
          <h6>Fuel mix</h6>
          <ChartWrap height={220}>
            <PieChart>
              <Pie data={fuelMix} dataKey="pct" nameKey="fuel" cx="50%" cy="50%" outerRadius={72} label={({ fuel, pct }) => `${fuel} ${pct?.toFixed?.(0) ?? pct}%`}>
                {fuelMix.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => [`${formatIndianNumber(v, 2)}%`, 'Share']} />
            </PieChart>
          </ChartWrap>
        </div>
      )}
    </div>
  );
};

export const ProfitabilityCharts = ({ data }) => {
  const trend = data.margin_trend || [];
  const categories = data.expense_categories || [];
  const comparison = data.comparison || {};

  const compData = comparison.current
    ? [
        { label: 'Sales', current: comparison.current.sales, prior: comparison.prior?.sales },
        { label: 'Gross margin', current: comparison.current.gross_margin, prior: comparison.prior?.gross_margin },
        { label: 'Expenses', current: comparison.current.expenses, prior: comparison.prior?.expenses },
      ]
    : [];

  return (
    <div className="admin-bi-chart-grid">
      {trend.length > 0 && (
        <div className="admin-bi-chart-card admin-bi-chart-card--wide">
          <h6>Margin trend</h6>
          <ChartWrap height={240}>
            <ComposedChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tickFormatter={formatAxisCurrency} tick={{ fontSize: 11 }} width={56} />
              <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} width={44} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="sales" name="Sales" fill="#93c5fd" />
              <Line yAxisId="right" type="monotone" dataKey="margin_pct" name="Margin %" stroke="#16a34a" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ChartWrap>
        </div>
      )}
      {categories.length > 0 && (
        <div className="admin-bi-chart-card">
          <h6>Expense breakdown</h6>
          <ChartWrap height={220}>
            <PieChart>
              <Pie data={categories} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={72} label={({ category, pct }) => `${(category || '').slice(0, 12)} ${pct?.toFixed?.(0) ?? 0}%`}>
                {categories.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => [formatInrAmount(v), 'Amount']} />
            </PieChart>
          </ChartWrap>
        </div>
      )}
      {compData.length > 0 && (
        <div className="admin-bi-chart-card">
          <h6>Current vs prior period</h6>
          <ChartWrap height={220}>
            <BarChart data={compData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tickFormatter={formatAxisCurrency} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={90} />
              <Tooltip formatter={(v) => formatInrAmount(v)} />
              <Legend />
              <Bar dataKey="current" name="Current" fill="#2563eb" radius={[0, 4, 4, 0]} />
              <Bar dataKey="prior" name="Prior" fill="#94a3b8" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ChartWrap>
        </div>
      )}
    </div>
  );
};

export const StaffingCharts = ({ data }) => {
  const trend = data.staffing_trend || data.daily_breakdown || [];
  const chartData = [...trend].reverse();

  if (!chartData.length) return null;

  return (
    <div className="admin-bi-chart-grid">
      <div className="admin-bi-chart-card admin-bi-chart-card--wide">
        <h6>Emp / nozzle ratio over time</h6>
        <ChartWrap height={220}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis yAxisId="ratio" domain={[0, 'auto']} tick={{ fontSize: 11 }} width={36} />
            <YAxis yAxisId="sales" orientation="right" tickFormatter={formatAxisCurrency} tick={{ fontSize: 11 }} width={56} />
            <Tooltip />
            <Legend />
            <Line yAxisId="ratio" type="monotone" dataKey="emp_per_nozzle" name="Emp/nozzle" stroke="#2563eb" strokeWidth={2} dot={false} />
            <Bar yAxisId="sales" dataKey="daily_sales" name="Daily sales" fill="#cbd5e1" />
          </ComposedChart>
        </ChartWrap>
        <p className="small text-muted mb-0 mt-1">Target band: 0.85 – 1.2 employees per active nozzle</p>
      </div>
    </div>
  );
};

export const ExpenseAnomalyCharts = ({ data }) => {
  const series = data.monthly_series || [];

  if (!series.length) return null;

  return (
    <div className="admin-bi-chart-grid">
      <div className="admin-bi-chart-card admin-bi-chart-card--wide">
        <h6>Monthly expenses vs rolling average</h6>
        <ChartWrap height={240}>
          <ComposedChart data={series}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={50} />
            <YAxis tickFormatter={formatAxisCurrency} tick={{ fontSize: 11 }} width={56} />
            <Tooltip formatter={(v) => (v != null ? formatInrAmount(v) : '—')} />
            <Legend />
            <Bar dataKey="expenses" name="Expenses" radius={[4, 4, 0, 0]}>
              {series.map((entry, i) => (
                <Cell key={i} fill={entry.is_anomaly ? '#dc2626' : '#2563eb'} />
              ))}
            </Bar>
            <Line type="monotone" dataKey="rolling_avg" name="Rolling avg" stroke="#d97706" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ChartWrap>
      </div>
    </div>
  );
};

export const CashCharts = ({ data }) => {
  const trend = data.cash_trend || [];
  const byEmployee = (data.by_employee || []).slice(0, 10);

  return (
    <div className="admin-bi-chart-grid">
      {trend.length > 0 && (
        <div className="admin-bi-chart-card admin-bi-chart-card--wide">
          <h6>Daily cash short vs excess</h6>
          <ChartWrap height={220}>
            <BarChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis tickFormatter={formatAxisCurrency} tick={{ fontSize: 11 }} width={56} />
              <Tooltip formatter={(v) => formatInrAmount(v)} />
              <Legend />
              <Bar dataKey="short" name="Short" fill="#dc2626" stackId="a" />
              <Bar dataKey="excess" name="Excess" fill="#16a34a" stackId="a" />
            </BarChart>
          </ChartWrap>
        </div>
      )}
      {byEmployee.length > 0 && (
        <div className="admin-bi-chart-card">
          <h6>Net variance by employee</h6>
          <ChartWrap height={Math.max(200, byEmployee.length * 28)}>
            <BarChart data={byEmployee} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tickFormatter={formatAxisCurrency} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="employee" tick={{ fontSize: 10 }} width={100} />
              <Tooltip formatter={(v) => formatInrAmount(v)} />
              <Bar dataKey="net_variance" name="Net" radius={[0, 4, 4, 0]}>
                {byEmployee.map((entry, i) => (
                  <Cell key={i} fill={(entry.net_variance || 0) < 0 ? '#dc2626' : '#16a34a'} />
                ))}
              </Bar>
            </BarChart>
          </ChartWrap>
        </div>
      )}
    </div>
  );
};

export const AllocationCharts = ({ data }) => {
  const pairs = (data.pairs || []).slice(0, 12);

  if (!pairs.length) return null;

  const chartData = pairs.map((p) => ({
    ...p,
    label: `${(p.employee || '').split(' ')[0]} / ${(p.nozzle || '').slice(0, 8)}`,
  }));

  return (
    <div className="admin-bi-chart-grid">
      <div className="admin-bi-chart-card admin-bi-chart-card--wide">
        <h6>Rank gap by employee–nozzle pair</h6>
        <ChartWrap height={240}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 9 }} angle={-30} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="rank_gap" name="Rank gap" fill="#7c3aed" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartWrap>
      </div>
    </div>
  );
};

export const NozzleCharts = ({ data }) => {
  const share = (data.by_share || []).slice(0, 10);
  const topSales = (data.top_by_sales || data.nozzles || []).slice(0, 10).map((row) => ({
    name: row.name || row.nozzle_name || row.label,
    sales: row.value ?? row.sales ?? row.amount ?? 0,
  }));

  return (
    <div className="admin-bi-chart-grid">
      {share.length > 0 && (
        <div className="admin-bi-chart-card">
          <h6>Sales share</h6>
          <ChartWrap height={220}>
            <PieChart>
              <Pie data={share} dataKey="pct" nameKey="name" cx="50%" cy="50%" outerRadius={72} label={({ name, pct }) => `${(name || '').slice(0, 10)} ${pct?.toFixed?.(0) ?? 0}%`}>
                {share.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ChartWrap>
        </div>
      )}
      {topSales.length > 0 && (
        <div className="admin-bi-chart-card">
          <h6>Top nozzles by sales</h6>
          <ChartWrap height={220}>
            <BarChart data={topSales} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tickFormatter={formatAxisCurrency} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={80} />
              <Tooltip formatter={(v) => formatInrAmount(v)} />
              <Bar dataKey="sales" fill="#0891b2" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ChartWrap>
        </div>
      )}
    </div>
  );
};

export const EmployeeCharts = ({ data }) => {
  const employees = (data.employees || []).slice(0, 12);

  if (!employees.length) return null;

  return (
    <div className="admin-bi-chart-grid">
      <div className="admin-bi-chart-card admin-bi-chart-card--wide">
        <h6>Sales per hour by attendant</h6>
        <ChartWrap height={Math.max(220, employees.length * 26)}>
          <BarChart data={employees} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis type="number" tickFormatter={formatAxisCurrency} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
            <Tooltip formatter={(v) => `${formatInrAmount(v)}/hr`} />
            <Bar dataKey="sales_per_hour" name="₹/hour" radius={[0, 4, 4, 0]}>
              {employees.map((entry, i) => {
                const tierColors = { star: '#16a34a', solid: '#2563eb', developing: '#d97706', review: '#dc2626' };
                return <Cell key={i} fill={tierColors[entry.tier] || '#64748b'} />;
              })}
            </Bar>
          </BarChart>
        </ChartWrap>
      </div>
    </div>
  );
};
